/**
 * Dashboard Page
 *
 * Protected page that shows different content based on user status
 * For active users: renders Directory inline
 * For registered users: renders CompleteProfile inline
 */

import { useAuth, useUserStatus } from '../../hooks/useAuth';
import { Directory } from './Directory';
import { CompleteProfile } from './CompleteProfile';

export function Dashboard() {
  const { user } = useAuth();
  const { status } = useUserStatus();

  if (!user) {
    return null;
  }

  // Renderizado inline según estado
  if (status === 'active') {
    return <Directory />;
  }

  if (status === 'registered') {
    return <CompleteProfile />;
  }

  // Otros estados: mostrar información
  return (
    <div>
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
            <div class="px-4 py-5 sm:px-6">
              <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                ¡Bienvenido!
              </h2>
            </div>

            <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
              {/* Status-based content */}
              {status === 'pending_approval' && (
                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div class="flex">
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-yellow-800">
                        Tu perfil está en revisión
                      </h3>
                      <div class="mt-2 text-sm text-yellow-700">
                        <p>Hemos recibido tu solicitud y está siendo revisada por nuestro equipo. Te notificaremos por email cuando sea aprobada.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {status === 'approved_pending_payment' && (
                <div class="bg-green-50 border-l-4 border-green-400 p-4">
                  <div class="flex">
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-green-800">
                        ¡Tu perfil ha sido aprobado!
                      </h3>
                      <div class="mt-2 text-sm text-green-700">
                        <p>Para activar tu cuenta, necesitas completar el pago anual de membresía.</p>
                        <a href="/pago-pendiente" class="mt-2 inline-block font-medium underline">
                          Ver instrucciones de pago →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {status === 'rejected' && (
                <div class="bg-red-50 border-l-4 border-red-400 p-4">
                  <div class="flex">
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-red-800">
                        Tu solicitud ha sido rechazada
                      </h3>
                      <div class="mt-2 text-sm text-red-700">
                        <p>{user.rejection_reason || 'Por favor, contacta con el administrador para más información.'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {status === 'inactive' && (
                <div class="bg-gray-50 border-l-4 border-gray-400 p-4">
                  <div class="flex">
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-gray-800">
                        Tu cuenta está inactiva
                      </h3>
                      <div class="mt-2 text-sm text-gray-700">
                        <p>Tu suscripción ha expirado. Por favor, renueva tu membresía para continuar accediendo al directorio.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User info */}
            <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl class="sm:divide-y sm:divide-gray-200">
                <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">Email</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.email}</dd>
                </div>
                <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">Fecha de registro</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {new Date(user.created_at).toLocaleDateString('es-ES')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
    </div>
  );
}
